import React, {useMemo, useRef, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";

import ContentContainer from "../../components/ContentContainer/ContentContainer";
import PointsPredictionListContainer from "./PointsPredictionListContainer";
import PointsPredictionTOTWContainer from "./PointsPredictionTOTWContainer";
import PointsPredictionTeamsCard, {WIDE_TEAMS_QUERY} from "./PointsPredictionTeamsCard";
import {useMediaQuery} from "../../useMediaQuery";
import {buildTeamRows, sortTeamRows, defaultSortDirection, orderTeamRows} from "./teamUtils";
import {GameweekTabs} from "../../components/GameweekSelector/GameweekSelector";
import Spacer from "../../components/Spacer/Spacer";

import Tabs from "../../components/Tabs/Tabs";
import Loading from "../../components/Loading/Loading";
import InfoPopover from "../../components/InfoPopover/InfoPopover";

import "./PointsPrediction.css";
import "./PointsPredictionTeams.css";
import {useDataFetch} from "../../useDataFetch";
import PageTitle from "../../components/PageTitle/PageTitle";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleInfo} from "@fortawesome/free-solid-svg-icons";
import PillSwitch from "../../components/PillSwitch/PillSwitch";

// No column sorted (the teams are in strength order, or the order kept from another view)
const NO_SORT = {key: null, direction: null};

const PointsPrediction = (props) => {
	const { data: dataDump, loading: dataLoading, error } = useDataFetch('/fetch-prediction-data');
	const location = useLocation();
	const navigate = useNavigate();

	const subPages = [
		{key: "players", label: "Players", route: "/prediction"},
		{key: "teams", label: "Teams", route: "/prediction/teams"},
	];
	const activeSubPage = location.pathname.startsWith("/prediction/teams") ? "teams" : "players";

	// One gameweek for the whole page, shared by the Team of the Week, the player list and the
	// fixtures, and kept when switching between the Players and Teams tabs.
	const [selectedGameweek, setSelectedGameweek] = useState(null);
	const availableGameweeks = useMemo(
		() => Object.keys(dataDump?.points_predictions || {}).map(Number).sort((a, b) => a - b),
		[dataDump]
	);
	const gameweek = selectedGameweek ?? dataDump?.gameweek;

	// Clicking a player in the Team of the Week shows just that player in the player list, with
	// their breakdown open. In stacked layouts (where the toolbar is sticky) the list is scrolled
	// into view, just below the toolbar.
	const [focusedPlayer, setFocusedPlayer] = useState(null);
	const playerListRef = useRef(null);
	const playerSplitRef = useRef(null);
	const showPlayerInList = (player) => {
		setFocusedPlayer({...player, requestedAt: Date.now()}); // re-applies on a repeat click
		const list = playerListRef.current;
		const toolbar = playerSplitRef.current?.querySelector(".split-gameweek-tabs");
		if (!list || !toolbar) return;
		const toolbarStyle = window.getComputedStyle(toolbar);
		if (toolbarStyle.position !== "sticky") return;
		const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		window.scrollTo({
			top: list.getBoundingClientRect().top + window.scrollY
				- (parseFloat(toolbarStyle.top) + toolbar.offsetHeight),
			behavior: reduceMotion ? "auto" : "smooth",
		});
	};

	// Free Hit: the best team for each gameweek on its own. Wildcard: one squad picked for the
	// upcoming gameweek, optimised over the next few; later gameweeks preview that same squad.
	const [teamMode, setTeamMode] = useState("freehit");
	const wildcard = dataDump?.wildcard;
	const isWildcard = teamMode === "wildcard" && !!wildcard;
	const wildcardGameweeks = wildcard?.gameweeks || [];
	const wildcardRange = wildcardGameweeks.length
		? `GW${wildcardGameweeks[0]}–${wildcardGameweeks[wildcardGameweeks.length - 1]}`
		: "";
	const statusById = useMemo(() => {
		const players = dataDump?.points_predictions?.[dataDump?.gameweek] || [];
		return Object.fromEntries(players.map(p => [p.id, p.status]));
	}, [dataDump]);
	// Each player's fixtures per gameweek, e.g. {12: {6: "LEE(H)"}}, for the wildcard list
	const fixturesById = useMemo(() => {
		const result = {};
		Object.entries(dataDump?.points_predictions || {}).forEach(([gw, players]) => {
			players.forEach(p => {
				const opponents = [].concat(p.opponent_name ?? []);
				const venues = [].concat(p.home_away ?? []);
				result[p.id] = result[p.id] || {};
				result[p.id][gw] = opponents
					.map((opp, idx) => `${opp}(${venues[idx] === "home" ? "H" : "A"})`)
					.join(", ");
			});
		});
		return result;
	}, [dataDump]);
	const appearancesByIdAndGameweek = useMemo(() => {
		const result = {};
		Object.entries(dataDump?.points_predictions || {}).forEach(([gw, players]) => {
			players.forEach(p => {
				const fixturesWithMinutes = [].concat(p.points_minutes ?? []).filter(val => val > 0);
				result[p.id] = result[p.id] || {};
				result[p.id][gw] = fixturesWithMinutes.length;
			});
		});
		return result;
	}, [dataDump]);

	const teamModes = [
		{key: "freehit", label: "Free Hit"},
		{key: "wildcard", label: "Wildcard"},
	];

	// Teams sub-page: one card with the upcoming games (goals / clean sheets) or team strength.
	// Switching views keeps the teams in the order they're in, with no column marked as sorted, so
	// the next click on a column sorts it as if nothing was sorted before.
	const [teamView, setTeamView] = useState("goals");
	const wideTeams = useMediaQuery(WIDE_TEAMS_QUERY); // team strength and games side by side
	const [teamSort, setTeamSort] = useState(null);   // {key, direction}, or null: no column sorted
	const [teamOrder, setTeamOrder] = useState(null); // team codes, kept from before a view switch
	const teamTable = useMemo(
		() => buildTeamRows(dataDump?.team_strength, dataDump?.fixtures),
		[dataDump]
	);
	const sortedTeamRows = useMemo(
		() => teamSort
			? sortTeamRows(teamTable.rows, teamSort, teamView === "strength" ? "goals" : teamView)
			: orderTeamRows(teamTable.rows, teamOrder),
		[teamTable, teamSort, teamOrder, teamView]
	);
	const changeTeamView = (view) => {
		setTeamOrder(sortedTeamRows.map(row => row.team_code));
		setTeamSort(null);
		setTeamView(view);
	};
	// Clicking the sorted column flips it; another column sorts best first
	const sortTeamsBy = (key) => setTeamSort((current) => current?.key === key
		? {key, direction: current.direction === "asc" ? "desc" : "asc"}
		: {key, direction: defaultSortDirection(key)});

	const [showNews, setShowNews] = useState(() => {
		const currentDate = new Date();
		const startDate = new Date('2026-02-09T00:00:00');
		const endDate = new Date('2026-02-10T23:59:59');

		return currentDate >= startDate && currentDate <= endDate;
	});

	const infoIcon = (<FontAwesomeIcon icon={faCircleInfo} style={{color: "var(--color-info)", fontSize: 16}}/>)

	const explainerText = () => {
		return (
			<InfoPopover storageKey={"prediction"} title={"Player points predictions"}>
				<p>
					To help you pick the best players, these are the model's <b>Player Points Predictions</b>. For
					every player,
					the model predicts his goals, assists, defensive contributions, clean sheets, bonus and everything else that
					impacts a players points. The predictions are based on a combination of historical data and
					odds data, combined using ML models and statistics.
					<br/><br/>

					Below you see the <b>Team of the Week</b>, the optimal team selection for the upcoming week. Below that you
					can dive into each players' individual points predictions and how we got to that prediction.
					Switch to <b>Wildcard</b> to see the best squad to pick now for the next few gameweeks, and
					every player's predicted points over that period.
					<br/><br/>

					Switch to <b>Teams</b> to see how strong the model rates every team in attack and defence, and
					the expected goals and clean sheet chances for each upcoming fixture.
					<br/><br/>

					To see how these predictions compare to those of other websites, check the{" "}
					<Link to="/evaluation">Leaderboard here</Link>.
				</p>
			</InfoPopover>
		)
	}

	const newsSection = () => {
		if (!showNews) return null;

		return (
			<div className={"page-part-white"} style={{ paddingTop: "20px", paddingBottom: "20px" }}>
				<div className={"main-text"}>
					<div className="news-banner" role="status">
						<div className="news-banner-body">
							<div className="news-banner-icon">{infoIcon}</div>
							<span>
								<b>PSA: Due to a big incoming update I won't have DGW predictions before the deadline.</b> <br/> I'm working on a big update to make predictions available longer in advance, also for future gameweeks. An unfortunate side-effect is that this means for the upcoming gameweek I won't have updated predictions (due to it being a double gameweek). I'm sorry to all who use this website and want to thank all of you! I'll make sure to fix this ASAP so it won't happen again. Any questions, feel free to sent me a DM on Reddit/X.
							</span>
						</div>
						<button
							type="button"
							className="news-banner-close"
							aria-label="Dismiss"
							onClick={() => setShowNews(false)}
						>
							&#10005;
						</button>
					</div>
				</div>
			</div>
		);
	}

	const teamModeToggle = () => {
		if (!wildcard) return null;
		return <PillSwitch options={teamModes} value={teamMode} onChange={setTeamMode} label="Team type"/>;
	}

	const team_of_the_week = () => {
		return (
			<div className={"page-part-white"}>
				<div style={{
					display: "flex",
					justifyContent: "center",
					flexWrap: "nowrap"
				}}>
					<ContentContainer containerStyle={"content-container-flex-when-small-centered"}>
						<PointsPredictionTOTWContainer
							data={isWildcard ? wildcard.team_by_gw : dataDump['team_of_the_week']}
							gameweek={gameweek}
							availableGameweeks={availableGameweeks}
							dataLoading={dataLoading}
							title={isWildcard ? "Wildcard" : undefined}
							onPlayerClick={showPlayerInList}
						/>
					</ContentContainer>

				</div>
				<div className={"explainer-text"}>
					<div style={{maxWidth: "450px", margin: "0 auto", textAlign: "center"}}>
						{isWildcard ? (
							<p>
								The Wildcard shows the optimal squad to pick for GW{wildcardGameweeks[0]}, using a
								budget of 100m and optimised for the predicted points over {wildcardRange}. The
								later gameweeks only preview how this same squad would line up; they are not a
								transfer plan.
							</p>
						) : (
							<p>
								The Team Of The Week shows the optimal selection you can make for the upcoming
								gameweek, based on the player points predictions, using a budget of
								100m.
							</p>
						)}
					</div>
				</div>
			</div>
		)
	}

	const player_list = () => {
		return (
			<div className={"page-part-grey"} ref={playerListRef}>
				<div style={{
					display: "flex",
					justifyContent: "center",
					flexWrap: "wrap"
				}}>
					<ContentContainer containerStyle={"content-container-flex-when-small-centered"}>
						<PointsPredictionListContainer
							data={isWildcard ? wildcard.points_predictions : dataDump['points_predictions']}
							gameweek={gameweek}
							dataLoading={dataLoading}
							mode={isWildcard ? "wildcard" : "freehit"}
							wildcardGameweeks={wildcardGameweeks}
							statusById={statusById}
							fixturesById={fixturesById}
							appearancesByIdAndGameweek={appearancesByIdAndGameweek}
							focusedPlayer={focusedPlayer}
						/>
					</ContentContainer>
				</div>
				<div className={"explainer-text"}>
					<div style={{maxWidth: "450px", margin: "0 auto", textAlign: "center"}}>
						<p>
							Last updated: {dataDump['last_updated']}
						</p>
					</div>
				</div>

			</div>
		)
	}

	const subPageTabs = () => {
		return (
			<div className={"page-part-white"} style={{paddingBottom: "0"}}>
				{/* Tabs in the middle, the page info button on the right */}
				<div className="sub-tabs-row">
					<div className="sub-tabs" role="tablist">
						{subPages.map((page) => (
							<button
								key={page.key}
								role="tab"
								aria-selected={page.key === activeSubPage}
								className={`sub-tab ${page.key === activeSubPage ? "active" : "inactive"}`}
								onClick={() => navigate(page.route)}
							>
								{page.label}
							</button>
						))}
					</div>
					{explainerText()}
				</div>
			</div>
		)
	}

	const teamPredictions = () => {
		return (
			<div className={"page-part-white"}>
				<div style={{display: "flex", justifyContent: "center"}}>
					<ContentContainer containerStyle={"content-container-flex-when-small-centered teams-card"}>
						<PointsPredictionTeamsCard
							rows={sortedTeamRows}
							gameweeks={teamTable.gameweeks}
							view={teamView}
							onViewChange={changeTeamView}
							sort={teamSort ?? NO_SORT}
							onSort={sortTeamsBy}
						/>
					</ContentContainer>
				</div>
				<div className={"explainer-text"}>
					<div style={{maxWidth: "450px", margin: "0 auto", textAlign: "center"}}>
						{(wideTeams || teamView === "strength") && (
							<p>
								Attack and defence show the expected goals each team would score and concede
								against an average Premier League team. Teams are ranked (#) by the difference
								between the two.
							</p>
						)}
						{(wideTeams || teamView !== "strength") && (
							<p>
								Each team's upcoming games, with the goals they're expected to score (xG) or their
								chance of keeping a clean sheet, and the total over all of them (expected goals or
								clean sheets). Blue is better than average, red is worse.
							</p>
						)}
						<p>
							Click a column to sort by it. Last updated: {dataDump['last_updated']}
						</p>
					</div>
				</div>
			</div>
		)
	}

	const playerPredictions = () => {
		return (
			<div className="prediction-split" ref={playerSplitRef}>
				{/* One toolbar for both cards: team type, then gameweek */}
				<GameweekTabs
					gameweek={gameweek}
					availableGameweeks={availableGameweeks}
					onChange={setSelectedGameweek}
					anchorGameweek={wildcardGameweeks[0] ?? null}
					anchorLabel="WC"
					anchorActive={isWildcard}
					className="split-gameweek-tabs"
				>
					{teamModeToggle()}
				</GameweekTabs>
				{team_of_the_week()}
				<Spacer style={{padding: "15px"}}/>
				{player_list()}
			</div>
		)
	}

	return (
		<div>
			<div className={"page-part-grey"}>
				<PageTitle/>
			</div>
			<Tabs/>
			{subPageTabs()}
			{newsSection()}
			{dataLoading && <Loading/>}
			{!dataLoading && (activeSubPage === "teams" ? teamPredictions() : playerPredictions())}
		</div>
		);
	};

export default PointsPrediction;
